import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import type { BoardConfig, Post } from '../types/board.type.ts';
import { getFaqSubCategoryOptions } from '../constants/faq.ts';
import { getArchiveSubCategoryOptions } from '../constants/archive.ts';
import { FieldShell } from './FieldShell.tsx';
import { inputClass } from './fieldStyles.ts';

/**
 * 게시판 타입별 추가(meta) 입력 필드.
 *
 * config.type 으로 분기해 공지=카테고리, 업데이트=제품/버전,
 * 이벤트=기간/신청링크, 자료실=분류/하위 분류를 렌더한다. 값은 meta 객체로 평면 관리하고
 * 부모(BoardForm)가 setMeta 로 갱신한다.
 */
interface MetaFieldsProps {
  type: BoardType;
  config: BoardConfig<Post>;
  meta: Record<string, unknown>;
  errors: Record<string, string | undefined>;
  onChange: (key: string, value: unknown) => void;
}

export function MetaFields({
  type,
  config,
  meta,
  errors,
  onChange,
}: MetaFieldsProps) {
  if (type === 'notices') {
    return (
      <FieldShell
        label="카테고리"
        htmlFor="category"
        error={errors.category}
        required
      >
        <select
          id="category"
          value={(meta.category as string) ?? ''}
          onChange={(e) => onChange('category', e.target.value)}
          className={inputClass(errors.category)}
        >
          <option value="" disabled>
            선택하세요
          </option>
          {config.categories?.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </FieldShell>
    );
  }

  if (type === 'faqs') {
    const subCategoryOptions = getFaqSubCategoryOptions(meta.faqCategory);
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldShell label="대분류" htmlFor="faqCategory" error={errors.faqCategory} required>
          <select
            id="faqCategory"
            value={(meta.faqCategory as string) ?? ''}
            onChange={(e) => {
              onChange('faqCategory', e.target.value);
              onChange('faqSubCategory', '');
            }}
            className={inputClass(errors.faqCategory)}
          >
            <option value="" disabled>
              선택하세요
            </option>
            {config.categories?.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </FieldShell>
        <FieldShell label="중분류" htmlFor="faqSubCategory" error={errors.faqSubCategory} required>
          <select
            id="faqSubCategory"
            value={(meta.faqSubCategory as string) ?? ''}
            onChange={(e) => onChange('faqSubCategory', e.target.value)}
            className={inputClass(errors.faqSubCategory)}
            disabled={subCategoryOptions.length === 0}
          >
            <option value="" disabled>
              선택하세요
            </option>
            {subCategoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FieldShell>
      </div>
    );
  }

  if (type === 'archives') {
    const archiveSubOptions = getArchiveSubCategoryOptions(meta.archiveCategory);
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldShell label="분류" htmlFor="archiveCategory" error={errors.archiveCategory} required>
          <select
            id="archiveCategory"
            value={(meta.archiveCategory as string) ?? ''}
            onChange={(e) => {
              onChange('archiveCategory', e.target.value);
              onChange('archiveSubCategory', '');
            }}
            className={inputClass(errors.archiveCategory)}
          >
            <option value="" disabled>
              선택하세요
            </option>
            {config.categories?.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </FieldShell>
        <FieldShell
          label="하위 분류"
          htmlFor="archiveSubCategory"
          error={errors.archiveSubCategory}
          required={archiveSubOptions.length > 0}
          hint={archiveSubOptions.length === 0 && meta.archiveCategory ? '이 분류는 하위 분류가 없습니다.' : undefined}
        >
          <select
            id="archiveSubCategory"
            value={(meta.archiveSubCategory as string) ?? ''}
            onChange={(e) => onChange('archiveSubCategory', e.target.value)}
            className={inputClass(errors.archiveSubCategory)}
            disabled={archiveSubOptions.length === 0}
          >
            <option value="" disabled>
              선택하세요
            </option>
            {archiveSubOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FieldShell>
      </div>
    );
  }

  if (type === 'updates') {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldShell label="제품" htmlFor="productSlug" error={errors.productSlug} required>
          <select
            id="productSlug"
            value={(meta.productSlug as string) ?? ''}
            onChange={(e) => onChange('productSlug', e.target.value)}
            className={inputClass(errors.productSlug)}
          >
            <option value="" disabled>
              선택하세요
            </option>
            {config.categories?.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </FieldShell>
        <FieldShell label="버전" htmlFor="version" error={errors.version} hint="예: 2026.1.0">
          <input
            id="version"
            type="text"
            value={(meta.version as string) ?? ''}
            onChange={(e) => onChange('version', e.target.value)}
            placeholder="2026.1.0"
            className={inputClass(errors.version)}
          />
        </FieldShell>
      </div>
    );
  }

  if (type === 'events') {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FieldShell label="시작일시" htmlFor="startsAt" error={errors.startsAt} required>
            <input
              id="startsAt"
              type="datetime-local"
              value={(meta.startsAt as string) ?? ''}
              onChange={(e) => onChange('startsAt', e.target.value)}
              className={inputClass(errors.startsAt)}
            />
          </FieldShell>
          <FieldShell label="종료일시" htmlFor="endsAt" error={errors.endsAt} required>
            <input
              id="endsAt"
              type="datetime-local"
              value={(meta.endsAt as string) ?? ''}
              onChange={(e) => onChange('endsAt', e.target.value)}
              className={inputClass(errors.endsAt)}
            />
          </FieldShell>
        </div>
        <FieldShell
          label="신청 링크"
          htmlFor="applyUrl"
          error={errors.applyUrl}
          hint="외부 신청 폼 URL (선택)"
        >
          <input
            id="applyUrl"
            type="url"
            value={(meta.applyUrl as string) ?? ''}
            onChange={(e) => onChange('applyUrl', e.target.value)}
            placeholder="https://"
            className={inputClass(errors.applyUrl)}
          />
        </FieldShell>
      </div>
    );
  }

  return null;
}
